import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { effectivePrice } from "@/lib/pricing";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const SORTS = {
  recent: "Most Recent",
  price_asc: "Price ↑",
  price_desc: "Price ↓",
  az: "A–Z",
};

export default function Products() {
  const [params] = useSearchParams();
  const category = params.get("category") || "";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("recent");
  const [q, setQ] = useState("");

  useEffect(() => {
    setLoading(true);
    const url = category ? `/products?category=${encodeURIComponent(category)}` : "/products";
    api.get(url).then(({ data }) => setProducts(data)).catch(() => {}).finally(() => setLoading(false));
  }, [category]);

  let list = products.filter((p) => {
    if (!q) return true;
    const ql = q.toLowerCase();
    return p.name.toLowerCase().includes(ql) || (p.code || "").toLowerCase().includes(ql);
  });
  const catPriority = (c) => {
    const s = (c || "").toLowerCase();
    if (s.includes("exotic flower")) return 0;
    if (s.includes("flower")) return 1;
    return 2;
  };
  list = [...list].sort((a, b) => {
    if (!category) {
      const pa = catPriority(a.category), pb = catPriority(b.category);
      if (pa !== pb) return pa - pb;
    }
    if (sort === "price_asc") return effectivePrice(a) - effectivePrice(b);
    if (sort === "price_desc") return effectivePrice(b) - effectivePrice(a);
    if (sort === "az") return a.name.localeCompare(b.name);
    return (b.created_at || "").localeCompare(a.created_at || "");
  });

  return (
    <div className="px-4 py-6">
      <h1 className="font-heading font-black uppercase text-3xl text-[#F0EDEA] mb-1">{category || "Menu"}</h1>
      <p className="text-[#A8A2A0] text-sm mb-5">{list.length} product{list.length !== 1 ? "s" : ""}</p>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A2A0]" />
          <input
            data-testid="product-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search products..."
            className="w-full bg-[#1C1C1C] border border-[#333] rounded-xl h-11 pl-10 pr-4 text-[#F0EDEA] placeholder-[#A8A2A0] focus:border-[#E60000] outline-none"
          />
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="sm:w-48 bg-[#1C1C1C] border-[#333] text-[#F0EDEA] h-11 rounded-xl" data-testid="sort-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#1C1C1C] border-[#333] text-[#F0EDEA]">
            {Object.entries(SORTS).map(([k, v]) => (
              <SelectItem key={k} value={k} className="focus:bg-[#E60000] focus:text-white" data-testid={`sort-${k}`}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-[#A8A2A0] text-center py-16">Loading...</div>
      ) : list.length === 0 ? (
        <div className="text-[#A8A2A0] text-center py-16" data-testid="no-products">No products found.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="products-grid">
          {list.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </div>
  );
}
